<template>
  <div class="fts-approval-request fts-detail-view-form">
    <div class="ar-head">
      <fts-btn text icon class="ar-back" aria-label="Back" @click.native.prevent="$router.back()">
        <font-awesome-icon :icon="['fas', 'angle-left']" />
      </fts-btn>
      <h1 class="ar-title">{{ request ? request.module : 'Approval request' }}</h1>
      <span class="ar-chip" v-if="request && expiryText">{{ expiryText }}</span>
    </div>

    <div class="ar-loading" v-if="loading"><fts-spinner /></div>
    <div class="ar-banner ar-banner--error" role="alert" v-else-if="loadError">{{ loadError }}</div>

    <template v-else-if="request">
      <a class="ar-entry" href="#" @click.prevent="openEntry" v-if="request.entryId">
        <span class="ar-entry-title">{{ request.entryTitle || request.module }}</span>
        <span class="ar-entry-hint">View entry</span>
      </a>

      <div class="ar-banner ar-banner--warn" role="status" v-if="otherWorkspace">
        This request belongs to another workspace. Switch to it to approve or reject.
      </div>
      <div class="ar-banner ar-banner--warn" role="status" v-if="request.status === 'stale'">
        <b>No longer valid.</b> {{ request.staleReason || 'The entry changed after the request was made.' }}
        The AI can send a new request.
      </div>
      <div class="ar-banner ar-banner--ok" role="status" v-else-if="request.status === 'approved'">
        <b>Approved</b> by {{ request.decidedBy }}{{ decidedText }}.
      </div>
      <div class="ar-banner ar-banner--muted" role="status" v-else-if="request.status === 'rejected'">
        <b>Rejected</b> by {{ request.decidedBy }}{{ decidedText }}<template v-if="request.rejectReason">: “{{ request.rejectReason }}”</template><template v-else>.</template>
      </div>
      <div class="ar-banner ar-banner--muted" role="status" v-else-if="request.status === 'expired' || request.status === 'cancelled'">
        This request was {{ request.status }}.
      </div>
      <div class="ar-banner ar-banner--warn" role="status" v-else-if="request.status === 'failed'">
        The last approve did not go through{{ request.failure ? ': ' + request.failure : '' }}. You can try again.
      </div>

      <section class="ar-strip" aria-label="Approval requested">
        <div class="ar-strip-head">
          <span class="ar-ai" aria-hidden="true">AI</span>
          <span class="ar-strip-title">Approval requested</span>
          <span class="ar-strip-meta">by {{ agent }}<template v-if="requester"> for {{ requester }}</template><template v-if="createdText"> · {{ createdText }}</template></span>
        </div>
        <p class="ar-summary" v-if="request.summary">{{ request.summary }}</p>
      </section>

      <div class="ar-form-title">
        <font-awesome-icon :icon="['fas', 'lock']" />
        {{ request.activity }} · read-only
      </div>

      <fts-classification
        :locus-id="request.entryId"
        :vector-id="request.moduleId"
        :users="users"
        :activity="request.activity"
        :activity-id="request.activityId"
        :view="true"
        :view-data="viewData"
        :loading="formLoading"
        :comment-section="false"
      />

      <div class="dv-form-afooter ar-footer">
        <fts-workflow-bar
          :module-id="request.moduleId"
          :states="states"
          :activities="[]"
          :activity="activity"
          :current-state="currentState"
          :next-states="nextStates"
          :form-data="{}"
          :assign-right="false"
          :comment-right="false"
          :design-mode="false"
          class="dv-form-afooter-activity-flow"
        />

        <div class="dv-form-afooter-action ar-actions" v-if="canDecide">
          <template v-if="rejecting">
            <label class="ar-reason-label" for="ar-reason">Reason <span>(optional)</span></label>
            <textarea id="ar-reason" class="ar-reason" rows="2" v-model="reason"
              placeholder="Sent back to the AI that asked"></textarea>
            <div class="ar-action-row">
              <fts-btn text text-transform-init :disabled="busy" @click.native.prevent="rejecting = false">Cancel</fts-btn>
              <fts-btn color="danger" text-transform-init :loading="busy" @click.native.prevent="reject">Reject</fts-btn>
            </div>
          </template>
          <div class="ar-action-row" v-else>
            <fts-btn outline color="danger" text-transform-init :disabled="busy" @click.native.prevent="rejecting = true">Reject</fts-btn>
            <fts-btn
              text-transform-init
              :color="(activity && activity.color) || 'primary'"
              :emoji="activity && activity.emoji"
              :loading="busy"
              class="btn-activity"
              @click.native.prevent="approve"
            >{{ request.activity }}</fts-btn>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import FTSBtn from '../elements/FTSBtn';
import FTSClassification from '../elements/FTSClassification';
import FTSSpinner from '../elements/FTSSpinner';
import FTSWorkflowBar from '../elements/FTSWorkflowBar';
import { clientStore } from '../store/clientStore';
import {
  approveApprovalRequest,
  expiresInText,
  findState,
  getActivityForm,
  getApprovalRequest,
  isOpen,
  overlayValues,
  rejectApprovalRequest,
} from './approvalApi';

/**
 * The person's side of an approval request (docs/teammate-human-approval-request-proposal.md,
 * HA-R12/R18): the activity's own form, read-only, laid out as the detail view lays out an
 * activity, with an "Approval requested" strip above it and the detail view's footer below -
 * current state → next state on the left, Reject and the activity's own button on the right.
 * Approve is one step: the server runs the activity as the approver with exactly these values.
 */
export default {
  name: 'FTSApprovalRequest',
  components: {
    'fts-btn': FTSBtn,
    'fts-classification': FTSClassification,
    'fts-spinner': FTSSpinner,
    'fts-workflow-bar': FTSWorkflowBar,
  },
  data() {
    return {
      request: null,
      loading: true,
      loadError: null,
      formLoading: true,
      form: { rows: [{ items: [] }] },
      formData: {},
      formHeader: null,
      formStates: [],
      busy: false,
      rejecting: false,
      reason: '',
    };
  },
  computed: {
    requestId() {
      return this.$route.params.id;
    },
    users() {
      return clientStore.getters.workspaceUsers;
    },
    states() {
      const states = clientStore.getters.workspaceStates || [];
      return this.request ? states.filter((s) => s.module == this.request.moduleId) : [];
    },
    viewData() {
      return { form: this.form, formData: this.formData };
    },
    activity() {
      const fromForm = this.formHeader && this.formHeader.activity;
      return Object.assign({ id: this.request.activityId, name: this.request.activity }, fromForm || {});
    },
    currentState() {
      const fromForm = this.formHeader && this.formHeader.state;
      return fromForm || findState(this.states, this.request.fromState);
    },
    nextStates() {
      if (this.formStates.length) return this.formStates;
      return (this.request.nextStates || []).map((name) => findState(this.states, name)).filter((s) => s);
    },
    otherWorkspace() {
      const ws = clientStore.state.workspace;
      return !!(ws && this.request && ws.id != this.request.workspaceId);
    },
    canDecide() {
      return !!this.request && this.request.canDecide !== false && isOpen(this.request) && !this.otherWorkspace;
    },
    agent() {
      const by = this.request.requestedBy || {};
      return by.agent || 'AI agent';
    },
    requester() {
      const by = this.request.requestedBy || {};
      return by.displayName || by.username || null;
    },
    createdText() {
      return this.request.createdAt ? this.timeText(this.request.createdAt) : null;
    },
    decidedText() {
      return this.request.decidedAt ? ` · ${this.timeText(this.request.decidedAt)}` : '';
    },
    expiryText() {
      return isOpen(this.request) ? expiresInText(this.request.expiresAt) : null;
    },
  },
  watch: {
    requestId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    timeText(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    },
    async load() {
      this.loading = true;
      this.loadError = null;
      this.rejecting = false;
      this.reason = '';
      try {
        const res = await getApprovalRequest(this.requestId);
        this.request = res.data;
      } catch (err) {
        this.request = null;
        this.loadError = (err.response && err.response.data && err.response.data.message) || 'This approval request could not be opened.';
      } finally {
        this.loading = false;
      }
      if (this.request) this.loadForm();
    },
    async loadForm() {
      this.formLoading = true;
      const values = this.request.values || {};
      try {
        const res = await getActivityForm({
          moduleId: this.request.moduleId,
          entryId: this.request.entryId,
          activityId: this.request.activityId,
        });
        const data = res.data || {};
        const classification = data.classificationForm || {};
        this.formHeader = data.header || null;
        this.formStates = ((data.footer && data.footer.states) || [])
          .map((id) => findState(this.states, id)).filter((s) => s);
        this.form = classification.design || { rows: [{ items: this.request.fields || [] }] };
        this.formData = overlayValues(classification.default, values);
      } catch (err) {
        // The form could not be read as the approver (another workspace, a removed design): the
        // request's own field list still shows every value it would submit.
        this.form = { rows: [{ items: this.request.fields || [] }] };
        this.formData = overlayValues({}, values);
      } finally {
        this.formLoading = false;
      }
    },
    async approve() {
      if (this.busy) return;
      this.busy = true;
      try {
        const res = await approveApprovalRequest(this.request.requestId);
        const done = res.data || {};
        this.$toasted.global.success({
          message: done.state ? `Approved · now ${done.state}` : 'Approved',
        });
        await this.load();
      } catch (err) {
        this.$toasted.global.error({ response: err.response, message: 'Could not approve this request.' });
        await this.load();
      } finally {
        this.busy = false;
      }
    },
    async reject() {
      if (this.busy) return;
      this.busy = true;
      try {
        await rejectApprovalRequest(this.request.requestId, this.reason);
        this.$toasted.global.success({ message: 'Rejected. The AI that asked has been told.' });
        await this.load();
      } catch (err) {
        this.$toasted.global.error({ response: err.response, message: 'Could not reject this request.' });
        await this.load();
      } finally {
        this.busy = false;
      }
    },
    openEntry() {
      window.eventBus.$emit('quickViewData', { id: this.request.entryId, vectorId: this.request.moduleId });
    },
  },
};
</script>

<style lang="scss" scoped>
.fts-approval-request {
  max-width: 880px;
  margin: 0 auto;
  padding: 0.75rem 0.75rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ar-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ar-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 500;
  flex: 1 1 auto;
}
.ar-chip {
  font-size: 0.75rem;
  font-weight: 500;
  color: #424242;
  background: #f1f1f1;
  border-radius: 999px;
  padding: 0.25rem 0.625rem;
}
.ar-loading {
  padding: 3rem;
  display: flex;
  justify-content: center;
}
.ar-entry {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  text-decoration: none;
  color: inherit;
}
.ar-entry-title {
  font-size: 1.25rem;
  font-weight: 500;
}
.ar-entry-hint {
  font-size: 0.8125rem;
  color: var(--fts-primary, #1e6f8b);
}
.ar-banner {
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.45;
}
.ar-banner--warn { background: #fff4e0; color: #6b3e00; }
.ar-banner--ok { background: #eaf6e2; color: #2e5e12; }
.ar-banner--muted { background: #f1f1f1; color: #424242; }
.ar-banner--error { background: #fdecec; color: #8a1c1c; }
.ar-strip {
  background: #f7fbfd;
  border: 1px solid #d6eaf2;
  border-radius: 12px;
  padding: 0.75rem;
}
.ar-strip-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.ar-ai {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #1e6f8b;
  background: #e6f4fa;
  border-radius: 4px;
  padding: 0.0625rem 0.3125rem;
}
.ar-strip-title {
  font-weight: 500;
}
.ar-strip-meta {
  font-size: 0.8125rem;
  color: #616161;
}
.ar-summary {
  margin: 0.5rem 0 0;
  white-space: pre-line;
}
.ar-form-title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #616161;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
/* The detail view's activity footer (FTSDetailViewForm .dv-form-afooter): its rules are injected
   only once a detail view has mounted, so the page carries its own copy. */
.ar-footer {
  position: sticky;
  bottom: 0;
  padding: 1rem 0 0.75rem;
  display: flex;
  align-items: flex-end;
  background-color: var(--inistate-color-surface, #fff);
  z-index: 1;
}
.ar-footer .dv-form-afooter-activity-flow {
  flex: 1 0 40%;
  max-width: 40%;
  min-height: 2.125rem;
  padding-right: 0.75rem;
}
.ar-footer .dv-form-afooter-action {
  flex: 1 0 60%;
  max-width: 60%;
}
.ar-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}
.ar-action-row {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.ar-reason-label {
  align-self: stretch;
  font-size: 0.8125rem;
  font-weight: 500;
  > span { font-weight: 400; color: #616161; }
}
.ar-reason {
  align-self: stretch;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.5rem 0.625rem;
  font: inherit;
  resize: vertical;
}
@media (max-width: 640px) {
  .ar-footer { flex-direction: column; align-items: stretch; }
  .ar-footer .dv-form-afooter-activity-flow,
  .ar-footer .dv-form-afooter-action { max-width: 100%; flex-basis: auto; }
}
</style>
